import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { todayStr } from '../lib/format.js';

// ─── Maintenance View ─────────────────────────────────────────────────────────
export function MaintenanceView({ locationId, isManager }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', frequency: 'Monthly', frequency_days: 30, next_due: '' });
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', frequency: 'Monthly', frequency_days: 30, next_due: '' });

  const today = todayStr();
  const in30 = new Date(); in30.setDate(in30.getDate() + 30);
  const in30Str = in30.toISOString().slice(0, 10);

  // Never raise `loading` here: the placeholder collapses the page and resets scroll.
  async function load() {
    const data = await api('GET', `/api/maintenance?location_id=${locationId}`);
    setItems(data);
    setLoading(false);
  }

  useEffect(() => { setLoading(true); load(); }, [locationId]);

  async function handleComplete(id) {
    setCompleting(id);
    await api('POST', `/api/maintenance/${id}/complete`);
    await load();
    setCompleting(null);
  }

  function openEdit(item) {
    setShowAdd(false);
    setEditItem(item);
    setEditForm({ title: item.title, description: item.description || '', frequency: item.frequency, frequency_days: item.frequency_days, next_due: item.next_due });
  }

  async function handleEditSubmit(e) {
    e.preventDefault();
    await api('PUT', `/api/maintenance/${editItem.id}`, editForm);
    setEditItem(null);
    load();
  }

  async function handleDeleteItem(id) {
    if (!confirm('Delete this maintenance item?')) return;
    await api('DELETE', `/api/maintenance/${id}`);
    load();
  }

  async function handleAdd(e) {
    e.preventDefault();
    await api('POST', '/api/maintenance', { ...form, location_id: locationId });
    setShowAdd(false);
    setForm({ title: '', description: '', frequency: 'Monthly', frequency_days: 30, next_due: '' });
    load();
  }

  function itemStatus(item) {
    if (item.next_due < today) return 'overdue';
    if (item.next_due <= in30Str) return 'soon';
    return 'ok';
  }

  const statusStyle = {
    overdue: { card: 'border-l-4 border-l-red-500 bg-red-50', badge: 'bg-red-100 text-red-700', label: 'OVERDUE' },
    soon: { card: 'border-l-4 border-l-amber-400 bg-amber-50', badge: 'bg-amber-100 text-amber-700', label: 'DUE SOON' },
    ok: { card: 'border-l-4 border-l-green-400 bg-white', badge: 'bg-green-100 text-green-700', label: 'UPCOMING' },
  };

  const overdue = items.filter(i => itemStatus(i) === 'overdue');
  const soon = items.filter(i => itemStatus(i) === 'soon');
  const ok = items.filter(i => itemStatus(i) === 'ok');

  function ItemCard({ item }) {
    const st = itemStatus(item);
    const s = statusStyle[st];
    const daysStr = () => {
      const diff = Math.ceil((new Date(item.next_due) - new Date(today)) / 86400000);
      if (diff < 0) return `${Math.abs(diff)} days overdue`;
      if (diff === 0) return 'Due today';
      return `In ${diff} days`;
    };
    return (
      <div className={`rounded-xl p-4 ${s.card}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="font-semibold text-sm text-ink">{item.title}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.badge}`}>{s.label}</span>
            </div>
            {item.description && <p className="text-xs text-gray-500 mb-2 leading-relaxed">{item.description}</p>}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-medium text-gray-600">{item.frequency}</span>
              <span className={`text-xs font-semibold ${st === 'overdue' ? 'text-red-600' : st === 'soon' ? 'text-amber-600' : 'text-gray-500'}`}>
                {daysStr()} · {item.next_due}
              </span>
              {item.last_completed && <span className="text-xs text-gray-400">Last: {item.last_completed}</span>}
            </div>
          </div>
          {isManager && (
            <div className="flex gap-1.5 flex-shrink-0">
              <button onClick={() => openEdit(item)}
                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all">
                Edit
              </button>
              <button onClick={() => handleDeleteItem(item.id)}
                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-all">
                Del
              </button>
              <button onClick={() => handleComplete(item.id)} disabled={completing === item.id}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${completing === item.id ? 'opacity-50' : 'hover:opacity-90 active:scale-95'} ${st === 'overdue' ? 'bg-red-600 text-white' : 'bg-ink text-cream'}`}>
                {completing === item.id ? '...' : '✓ Done'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-bold text-lg text-ink">Maintenance Schedule</h2>
          <p className="text-sm text-gray-500">Equipment & facility maintenance tracking</p>
        </div>
        {isManager && (
          <button onClick={() => setShowAdd(s => !s)}
            className="bg-crimson text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-crimson-dark active:scale-95 transition-all">
            + Add Item
          </button>
        )}
      </div>

      {editItem && isManager && (
        <form onSubmit={handleEditSubmit} className="bg-white rounded-xl border border-crimson/30 p-5 mb-6 space-y-3 fade-in shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-ink">Edit: {editItem.title}</h3>
            <button type="button" onClick={() => setEditItem(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
          </div>
          <input value={editForm.title} onChange={e => setEditForm(f => ({...f, title: e.target.value}))} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30"
            placeholder="Item name" />
          <textarea value={editForm.description} onChange={e => setEditForm(f => ({...f, description: e.target.value}))} rows={2}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none resize-none"
            placeholder="Description (optional)" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Frequency Label</label>
              <select value={editForm.frequency} onChange={e => {
                const map = { 'Monthly':30, 'Weekly':7, 'Quarterly':90, 'Bi-annual':180, 'Annual':365 };
                setEditForm(f => ({...f, frequency: e.target.value, frequency_days: map[e.target.value] || f.frequency_days }));
              }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none">
                <option>Monthly</option><option>Quarterly</option><option>Bi-annual</option><option>Annual</option><option>Weekly</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Next Due Date</label>
              <input type="date" value={editForm.next_due} onChange={e => setEditForm(f => ({...f, next_due: e.target.value}))} required
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setEditItem(null)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 font-medium">Cancel</button>
            <button type="submit" className="flex-1 py-2 rounded-lg bg-crimson text-white text-sm font-semibold hover:bg-crimson-dark">Save Changes</button>
          </div>
        </form>
      )}

      {showAdd && isManager && (
        <form onSubmit={handleAdd} className="bg-white rounded-xl border border-gray-200 p-5 mb-6 space-y-3 fade-in shadow-sm">
          <h3 className="font-semibold text-sm text-ink">New Maintenance Item</h3>
          <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-crimson/30"
            placeholder="e.g. Replace water filter" />
          <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={2}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none resize-none"
            placeholder="Description (optional)" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Frequency Label</label>
              <select value={form.frequency} onChange={e => {
                const map = { 'Monthly':30, 'Weekly':7, 'Quarterly':90, 'Bi-annual':180, 'Annual':365 };
                setForm(f => ({...f, frequency: e.target.value, frequency_days: map[e.target.value] || 30 }));
              }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none">
                <option>Monthly</option><option>Quarterly</option><option>Bi-annual</option><option>Annual</option><option>Weekly</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Next Due Date</label>
              <input type="date" value={form.next_due} onChange={e => setForm(f => ({...f, next_due: e.target.value}))} required
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none" />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowAdd(false)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 font-medium">Cancel</button>
            <button type="submit" className="flex-1 py-2 rounded-lg bg-crimson text-white text-sm font-semibold hover:bg-crimson-dark">Add Item</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32 text-gray-400">Loading maintenance items...</div>
      ) : (
        <div className="space-y-6">
          {overdue.length > 0 && (
            <div>
              <p className="text-xs font-bold text-red-600 uppercase tracking-widest mb-3">🔴 Overdue</p>
              <div className="space-y-2">{overdue.map(i => <ItemCard key={i.id} item={i} />)}</div>
            </div>
          )}
          {soon.length > 0 && (
            <div>
              <p className="text-xs font-bold text-amber-600 uppercase tracking-widest mb-3">🟡 Due Within 30 Days</p>
              <div className="space-y-2">{soon.map(i => <ItemCard key={i.id} item={i} />)}</div>
            </div>
          )}
          {ok.length > 0 && (
            <div>
              <p className="text-xs font-bold text-green-600 uppercase tracking-widest mb-3">🟢 Upcoming</p>
              <div className="space-y-2">{ok.map(i => <ItemCard key={i.id} item={i} />)}</div>
            </div>
          )}
          {items.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <p className="text-4xl mb-3">🔧</p>
              <p className="font-medium">No maintenance items</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
